import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';
import type { StatutImmersionEnregistre } from './immersion-statut';

/**
 * Statut tel qu'il est **enregistré** : cycle de décision de l'immersion, qui
 * peut être en retard sur les dates. Le statut affiché et filtré se calcule,
 * lui, par `getImmersionStatus` (`lib/immersion-statut.ts`) — ne pas filtrer sur
 * ce champ, un écran et un filtre finiraient par se contredire.
 */
export type { StatutImmersionEnregistre } from './immersion-statut';

/** Voir `backend/src/domain/matching.ts` : trois états, une seule dispense. */
export type EtatCritere = 'MESURE' | 'NON_RENSEIGNE' | 'NON_EVALUABLE';

/**
 * Origine de la donnée d'un critère mesuré. `DEDUITE` est plafonnée au palier
 * médian côté moteur : elle ne vaut jamais une donnée déclarée compatible.
 */
export type SourceDonnee = 'DECLAREE' | 'DEDUITE';

export interface CritereAdequation {
  critere: string;
  /** Absent des propositions enregistrées avant l'introduction des états. */
  etat?: EtatCritere;
  points: number | null;
  maximum: number;
  motif: string;
  /** Absent des propositions enregistrées avant l'introduction des sources. */
  source?: SourceDonnee;
}

export interface CouvertureMatching {
  mesures: number;
  retenus: number;
  nonEvaluables: number;
}

export interface Adequation {
  besoinId: string;
  entrepriseId: string;
  entrepriseNom: string;
  posteRecherche: string | null;
  formationCode: string | null;
  placesRestantes: number;
  scoreNormalise: number;
  score: number;
  scoreBrut: number;
  maximumApplicable: number;
  criteres: CritereAdequation[];
  criteresMesures: string[];
  criteresNonRenseignes: string[];
  /** Idem : absents des propositions antérieures, d'où l'optionnalité. */
  criteresNonEvaluables?: string[];
  couverture?: CouvertureMatching;
  confiance: string;
  eligible: boolean;
  motifsIneligibilite: string[];
  avertissements: string[];
  contexte: string[];
  besoinCreeLe: string;
}

/**
 * Proposition telle que la rend la vue de croisement : l'adéquation calculée,
 * plus la commune et le statut déjà enregistré, ajoutés côté API. Ces deux
 * champs servent aux filtres de l'écran ; ils ne pèsent pas dans le score.
 */
export interface PropositionVue extends Adequation {
  entrepriseVille: string | null;
  statutEnregistre: StatutProposition | null;
}

export type SituationEvaluation = 'NORMAL' | 'RUPTURE_ENVISAGEE' | 'RUPTURE';

export const LIBELLES_SITUATION: Record<SituationEvaluation, string> = {
  NORMAL: 'La période en entreprise se poursuit normalement.',
  RUPTURE_ENVISAGEE: 'Une rupture est envisagée.',
  RUPTURE: 'Rupture du contrat / de la période en entreprise.',
};

/** Compte rendu d'entretien en entreprise, dans sa version enregistrée. */
export interface CompteRenduImmersion {
  id: string;
  dateVisite: string;
  compteRendu: string;
  problematiques: string;
  solutions: string;
  situation: SituationEvaluation;
  dateRupture: string | null;
  motifRupture: string | null;
  observations: string | null;
  faitA: string | null;
  faitLe: string | null;
  nomEntreprise: string | null;
  fonctionEntreprise: string | null;
  nomApprenant: string | null;
  nomOrganisme: string | null;
  documentId: string | null;
  modifieLe: string;
}

/**
 * Ce que les listes savent du compte rendu : assez pour choisir entre
 * « Évaluation » et « Modifier l'évaluation », pas plus. La donnée vient de
 * l'API, jamais de l'état de l'écran : après rechargement, le bouton doit dire
 * la même chose.
 */
export interface CompteRenduResume {
  id: string;
  dateVisite: string;
  situation: SituationEvaluation;
  modifieLe: string;
  documentId: string | null;
}

/** Réponse du chargement du compte rendu : préremplissage, saisie, tracés. */
export interface DossierCompteRendu {
  prerempli: {
    apprenant: { nom: string; prenom: string; dateEntree: string; formation: string };
    entreprise: { denomination: string; tuteur: string; fonctionTuteur: string };
    organisme: { lieu: string; representant: string };
  };
  evaluation: CompteRenduImmersion | null;
  signatures: { entreprise: string | null; apprenant: string | null; organisme: string | null } | null;
}

export interface LigneMatching {
  apprenantId: string;
  candidat: string;
  formation: string | null;
  /** Commune du candidat. */
  ville?: string | null;
  siteCode: string;
  rqth?: boolean;
  propositions: PropositionVue[];
  immersion: {
    id: string;
    entreprise: string;
    dateDebut: string | null;
    dateFin: string | null;
    sansImmersion: boolean;
    statut: StatutImmersionEnregistre;
  } | null;
}

export interface LigneImmersion {
  id: string;
  apprenantId: string;
  candidat: string;
  entreprise: string;
  entrepriseId: string;
  entrepriseTuteur: string | null;
  entrepriseTuteurFonction: string | null;
  formation: string | null;
  formationCode: string | null;
  /** Vides pour un positionnement sans immersion. */
  dateDebut: string | null;
  dateFin: string | null;
  sansImmersion: boolean;
  statut: StatutImmersionEnregistre;
  avisEntreprise: string | null;
  compteRendu: CompteRenduResume | null;
  /** Motifs de refus calculés par l'API, `null` quand le geste est possible. */
  refusOui: string | null;
  refusNon: string | null;
  refusModificationFin: string | null;
  refusSuppression: string | null;
}

/** Ligne d'immersion qui porte bien une période, à l'inverse d'un positionnement direct. */
export type LigneImmersionDatee = LigneImmersion & { dateDebut: string; dateFin: string };

export function estDatee(immersion: LigneImmersion): immersion is LigneImmersionDatee {
  return immersion.dateDebut !== null && immersion.dateFin !== null;
}

export interface PositionnementsCandidat {
  candidat: string;
  immersions: LigneImmersion[];
  /** Ce qui empêche un nouveau positionnement, `null` si rien. */
  refusPositionnement: string | null;
  /** Antenne du candidat : une entreprise créée depuis le positionnement s'y rattache. */
  site: string;
  entreprises: EntreprisePositionnable[];
}

export interface ResultatPositionnement {
  immersion: LigneImmersion;
  informes: number;
  message: string;
}

/** Saisie commune aux deux points d'entrée : le matching et la fiche d'entretien. */
export interface SaisiePositionnement {
  immersion: boolean;
  dateDebut?: string;
  dateFin?: string;
}

export type VueCandidat = 'COMPLETE' | 'PLACEMENT';

export interface ReponseMatching {
  vue: VueCandidat;
  candidats: LigneMatching[];
  immersions: LigneImmersion[];
  compteurs: {
    aPositionner: number;
    postesOuverts: number;
    immersionsEnCours: number;
  };
  poids: Record<string, number>;
  /** Libellés des critères, indexés par le nom de leur ligne de détail. */
  libelles: Record<string, string>;
}

export type StatutProposition = 'PROPOSEE' | 'ACCEPTEE' | 'REFUSEE' | 'EXPIREE';

export interface LigneSuggestion {
  id: string;
  score: number;
  statut: StatutProposition;
  origine: 'BATCH' | 'DEMANDE';
  criteres: CritereAdequation[];
  details: Adequation | null;
  creeLe: string;
  decideLe: string | null;
  motifRefus: string | null;
  candidat: {
    id: string;
    nom: string;
    siteCode: string;
    formation: string | null;
    age?: number | null;
    sexe?: 'F' | 'M' | null;
    rqth?: boolean;
    mobilite?: string | null;
  };
  entreprise: { id: string; denomination: string; ville: string | null; secteur: string | null };
  besoin: {
    id: string;
    postes: number;
    dateSouhaitee: string | null;
    profilRecherche: string | null;
    ouvertRqth: boolean;
  };
}

export interface FiltresSuggestions {
  sexe?: string;
  ageMin?: string;
  ageMax?: string;
  formationCode?: string;
  scoreMinimum?: string;
}

export interface ReponseSuggestions {
  vue: VueCandidat;
  suggestions: LigneSuggestion[];
  compteurs: { proposees: number; fortScore: number; candidatsConcernes: number };
  bareme: {
    seuilNotification: number;
    seuilProposition: number;
    poids: Record<string, number>;
  };
  libelles: Record<string, string>;
}

export interface TrancheHistorique {
  plage: string;
  proposees: number;
  acceptees: number;
  refusees: number;
  tauxAcceptation: number | null;
}

export interface ReponseHistorique {
  vue: VueCandidat;
  decisions: LigneSuggestion[];
  compteurs: Record<StatutProposition, number>;
  tranches: TrancheHistorique[];
}

export interface ResultatCalcul {
  candidatsExamines: number;
  besoinsExamines: number;
  propositionsCreees: number;
  propositionsActualisees: number;
  propositionsExpirees: number;
  notifications: number;
  ignore: boolean;
}

export function useMatching(site: string) {
  const parametres = new URLSearchParams({ site });
  return useQuery({
    queryKey: ['matching', site],
    queryFn: () => appelApi<ReponseMatching>(`/matching?${parametres.toString()}`),
  });
}

/**
 * Liste des immersions seule, sans le calcul de matching : sert l'écran
 * `periode-immersion`, dont le profil administratif n'a pas `com-matching`.
 */
export function useImmersions(site: string) {
  const parametres = new URLSearchParams({ site });
  return useQuery({
    queryKey: ['immersions', site],
    queryFn: () => appelApi<{ immersions: LigneImmersion[] }>(`/immersions?${parametres.toString()}`),
  });
}

function invalider(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: ['matching'] });
  void client.invalidateQueries({ queryKey: ['immersions'] });
  void client.invalidateQueries({ queryKey: ['positionnements-candidat'] });
  void client.invalidateQueries({ queryKey: ['contrats'] });
  void client.invalidateQueries({ queryKey: ['dossier'] });
  void client.invalidateQueries({ queryKey: ['matching-suggestions'] });
  void client.invalidateQueries({ queryKey: ['matching-historique'] });
  void client.invalidateQueries({ queryKey: ['apprenants'] });
  void client.invalidateQueries({ queryKey: ['parcours'] });
}

export function useSuggestions(site: string, filtres: FiltresSuggestions) {
  const parametres = new URLSearchParams({ site });
  for (const [cle, valeur] of Object.entries(filtres)) {
    if (valeur) parametres.set(cle, valeur);
  }
  const requete = parametres.toString();

  return useQuery({
    queryKey: ['matching-suggestions', requete],
    queryFn: () => appelApi<ReponseSuggestions>(`/matching/suggestions?${requete}`),
  });
}

export function useHistoriqueMatching(site: string) {
  const parametres = new URLSearchParams({ site });
  return useQuery({
    queryKey: ['matching-historique', site],
    queryFn: () => appelApi<ReponseHistorique>(`/matching/historique?${parametres.toString()}`),
  });
}

export function useRefuserProposition() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { id: string; motif: string }) =>
      appelApi<LigneSuggestion>(`/matching/propositions/${variables.id}/refus`, {
        methode: 'PATCH',
        corps: { motif: variables.motif },
      }),
    onSuccess: () => invalider(client),
  });
}

export function useLancerCalcul() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { site?: string; apprenantId?: string; entrepriseId?: string }) =>
      appelApi<ResultatCalcul>('/matching/calcul', { methode: 'POST', corps: variables }),
    onSuccess: () => invalider(client),
  });
}

export function usePositionner() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (
      variables: SaisiePositionnement & {
        apprenantId: string;
        entrepriseId: string;
        besoinId?: string;
      },
    ) =>
      appelApi<ResultatPositionnement>('/matching/positionnements', {
        methode: 'POST',
        corps: variables,
      }),
    onSuccess: () => invalider(client),
  });
}

/** Positionnements d'un candidat, pour la fiche d'entretien. */
/** Entreprise de l'antenne où le candidat peut être positionné. */
export interface EntreprisePositionnable {
  id: string;
  denomination: string;
  siret: string;
  codePostal: string | null;
  ville: string | null;
}

export function usePositionnementsCandidat(apprenantId: string | null) {
  return useQuery({
    queryKey: ['positionnements-candidat', apprenantId],
    queryFn: () =>
      appelApi<PositionnementsCandidat>(`/immersions/candidat/${apprenantId ?? ''}`),
    enabled: Boolean(apprenantId),
  });
}

/** Réponse de l'entreprise : « oui » ou « non ». */
export function useTrancherImmersion() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { id: string; statut: 'VALIDEE' | 'NON_VALIDEE' }) =>
      appelApi<{ message: string }>(`/immersions/${variables.id}`, {
        methode: 'PATCH',
        corps: { statut: variables.statut },
      }),
    onSuccess: () => invalider(client),
  });
}

export function useModifierFinImmersion() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { id: string; dateFin: string }) =>
      appelApi<LigneImmersion>(`/immersions/${variables.id}/fin`, {
        methode: 'PATCH',
        corps: { dateFin: variables.dateFin },
      }),
    onSuccess: () => invalider(client),
  });
}

export function useSupprimerImmersion() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      appelApi<{ message: string }>(`/immersions/${id}`, { methode: 'DELETE' }),
    onSuccess: () => invalider(client),
  });
}

/**
 * URL du compte rendu d'une immersion. Le navigateur porte le cookie de
 * session ; la route est gardée côté serveur pour les écrans qui voient déjà
 * l'immersion — l'URL n'accorde rien.
 */
export function urlCompteRenduImmersion(immersionId: string): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  return `${base}/immersions/${immersionId}/compte-rendu`;
}

/** Compte rendu enregistré et préremplissage, pour ouvrir le formulaire. */
export function useCompteRenduImmersion(immersionId: string | null) {
  return useQuery({
    queryKey: ['immersion-compte-rendu', immersionId],
    queryFn: () => appelApi<DossierCompteRendu>(`/immersions/${immersionId}/evaluation`),
    enabled: Boolean(immersionId),
  });
}

export interface SaisieCompteRendu {
  id: string;
  dateVisite: string;
  compteRendu: string;
  problematiques: string;
  solutions: string;
  situation: SituationEvaluation;
  dateRupture?: string | null;
  motifRupture?: string | null;
  observations?: string | null;
  faitA?: string | null;
  faitLe?: string | null;
  nomEntreprise?: string | null;
  fonctionEntreprise?: string | null;
  nomApprenant?: string | null;
  nomOrganisme?: string | null;
  /** Absent = tracé inchangé ; `null` = signature effacée. */
  signatureEntreprise?: string | null;
  signatureApprenant?: string | null;
  signatureOrganisme?: string | null;
}

/**
 * Enregistre le compte rendu et régénère son PDF. Même appel à la création et
 * à la correction : c'est le même document, il n'y en a qu'un par immersion.
 */
export function useEvaluerImmersion() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: SaisieCompteRendu) => {
      const { id, ...corps } = variables;
      return appelApi<CompteRenduImmersion & { nomFichier: string; creation: boolean }>(
        `/immersions/${id}/evaluations`,
        { methode: 'POST', corps },
      );
    },
    onSuccess: (_resultat, variables) => {
      invalider(client);
      void client.invalidateQueries({ queryKey: ['immersion-compte-rendu', variables.id] });
      // Le compte rendu s'affiche aussi au dossier apprenant et au dossier de
      // l'organisation : les trois écrans doivent voir la même version.
      void client.invalidateQueries({ queryKey: ['dossier'] });
      void client.invalidateQueries({ queryKey: ['repertoire'] });
    },
  });
}

/** Validation explicite qui transmet l'immersion au circuit des contrats. */
export function useValiderImmersion() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (immersionId: string) =>
      appelApi<{ message: string }>(`/immersions/${immersionId}`, {
        methode: 'PATCH',
        corps: { statut: 'VALIDEE' },
      }),
    onSuccess: () => invalider(client),
  });
}
